import React, { useState, useEffect } from 'react';
import { getDashboardStats } from '../../services/dashboard';
import BarChart from '../../components/graphs/BarChart';
import PieChart from '../../components/graphs/PieChart';
import {
    FaUsers, FaStore, FaChartLine, FaCalendarAlt,
    FaMapMarkerAlt, FaRupeeSign, FaUserPlus, FaUserCheck,
    FaShoppingCart, FaBoxOpen, FaMoneyBillWave, FaPercentage,
    FaRegClock, FaRegUser, FaIndustry, FaChartBar, FaGlobe, FaTags,
    FaUserTie, FaTractor, FaSatellite, FaBoxes, FaSeedling, FaMobileAlt
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import UserActivityTable from '../../components/tables/UserActivityTable';
import { Package } from 'lucide-react';
import ShowModal from '../../utils/ShowModal';
import BarChartSales from '../../components/graphs/BarChartSales';
import TopRegionsMap from '../../components/TopRegionsMap';

const StatsDashboardView = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({});

    const [modalIsOpen, setIsOpen] = useState(false);
    const [openingItem, setOpeningItem] = useState([]);
    const [openingItemTitle, setOpeningItemTitle] = useState("");


    const openModal = (data, title) => {
        setOpeningItem(data);
        setIsOpen(true);
        setOpeningItemTitle(title);
    };

    const closeModal = () => {
        setIsOpen(false);
        setOpeningItem([]);
        setOpeningItemTitle("");
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            let res = await getDashboardStats();
            if (res.status === 200) {
                setData(res.data);
            } else {
                toast.error("Unable to fetch Data");
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("Error fetching dashboard data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Icon mapping based on card title or type
    const getIconForCard = (title) => {
        const lowerTitle = title.toLowerCase();

        if (lowerTitle.includes('user')) {
            if (lowerTitle.includes('active')) return <FaUserCheck />;
            if (lowerTitle.includes('new')) return <FaUserPlus />;
            return <FaUsers />;
        }

        if (lowerTitle.includes('bazaar') || lowerTitle.includes('store')) {
            return <FaStore />;
        }

        if (lowerTitle.includes('revenue') || lowerTitle.includes('sales')) {
            return <FaMoneyBillWave />;
        }

        if (lowerTitle.includes('order')) {
            return <FaShoppingCart />;
        }

        if (lowerTitle.includes('inventory') || lowerTitle.includes('stock')) {
            return <FaBoxOpen />;
        }

        if (lowerTitle.includes('growth') || lowerTitle.includes('percentage')) {
            return <FaPercentage />;
        }

        if (lowerTitle.includes('time') || lowerTitle.includes('session')) {
            return <FaRegClock />;
        }

        if (lowerTitle.includes('industry') || lowerTitle.includes('sector')) {
            return <FaIndustry />;
        }

        if (lowerTitle.includes('metric') || lowerTitle.includes('analytics')) {
            return <FaChartBar />;
        }

        if (lowerTitle.includes('region') || lowerTitle.includes('global')) {
            return <FaGlobe />;
        }

        // Default icon
        return <FaChartLine />;
    };

    const CompactStatsCard = ({ title, value, icon, trend, color = 'green' }) => {
        // Color mapping with gradient options
        const colorMap = {
            green: {
                bgGradient: 'bg-gradient-to-br from-green-50 to-green-100',
                iconBg: 'bg-green-100',
                text: 'text-green-700',
                border: 'border-green-200',
                trendUpBg: 'bg-green-50',
                trendUpIcon: 'text-green-600',
                trendDownBg: 'bg-red-50',
                trendDownIcon: 'text-red-600'
            },
            teal: {
                bgGradient: 'bg-gradient-to-br from-teal-50 to-teal-100',
                iconBg: 'bg-teal-100',
                text: 'text-teal-700',
                border: 'border-teal-200',
                trendUpBg: 'bg-teal-50',
                trendUpIcon: 'text-teal-600',
                trendDownBg: 'bg-red-50',
                trendDownIcon: 'text-red-600'
            },
            emerald: {
                bgGradient: 'bg-gradient-to-br from-emerald-50 to-emerald-100',
                iconBg: 'bg-emerald-100',
                text: 'text-emerald-700',
                border: 'border-emerald-200',
                trendUpBg: 'bg-emerald-50',
                trendUpIcon: 'text-emerald-600',
                trendDownBg: 'bg-red-50',
                trendDownIcon: 'text-red-600'
            },
            amber: {
                bgGradient: 'bg-gradient-to-br from-amber-50 to-amber-100',
                iconBg: 'bg-amber-100',
                text: 'text-amber-700',
                border: 'border-amber-200',
                trendUpBg: 'bg-amber-50',
                trendUpIcon: 'text-amber-600',
                trendDownBg: 'bg-red-50',
                trendDownIcon: 'text-red-600'
            },
            violet: {
                bgGradient: 'bg-gradient-to-br from-violet-50 to-violet-100',
                iconBg: 'bg-violet-100',
                text: 'text-violet-700',
                border: 'border-violet-200',
                trendUpBg: 'bg-violet-50',
                trendUpIcon: 'text-violet-600',
                trendDownBg: 'bg-red-50',
                trendDownIcon: 'text-red-600'
            }
        };

        const colors = colorMap[color] || colorMap.green;

        // Use provided icon or get one based on title
        const cardIcon = icon || getIconForCard(title);

        return (
            <div className={`rounded-xl ${colors.bgGradient} ${colors.border} border p-3 hover:shadow-md transition-all duration-200 group hover:scale-[1.02]`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-xl ${colors.iconBg} group-hover:opacity-90 transition-opacity shadow-sm`}>
                            {React.cloneElement(cardIcon, { className: `${colors.text} text-xl` })}
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs font-bold text-gray-600 truncate">{title}</p>
                            <p className="text-lg font-bold text-gray-800 truncate mt-0.5">{value}</p>
                        </div>
                    </div>
                    {trend === 'up' && (
                        <div className={`flex items-center justify-center w-6 h-6 rounded-full ${colors.trendUpBg} ml-2 shadow-sm`}>
                            <FaChartLine className={`${colors.trendUpIcon} text-xs`} />
                        </div>
                    )}
                    {trend === 'down' && (
                        <div className={`flex items-center justify-center w-6 h-6 rounded-full ${colors.trendDownBg} ml-2 shadow-sm`}>
                            <FaChartLine className={`${colors.trendDownIcon} text-xs transform rotate-180`} />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const SectionHeader = ({ title, icon, subTitle = "" }) => (
        <div className="flex items-center space-x-2 mb-2">
            <div className="p-1 rounded-md bg-primary">
                {React.cloneElement(icon, { className: "text-white text-xs" })}
            </div>
            <div className="flex flex-col">
                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">{title}</h3>
                <p className="text-[0.6rem] text-gray-500">{subTitle}</p>
            </div>
        </div>
    );

    const getChartData = (rawData) => {
        const result = {};
        rawData?.forEach(({ state, user_count }) => {
            if (user_count > 0) result[state] = { user_count };
        });
        return result;
    };

    if (loading) {
        return (
            <div className="p-2">
                <div className="w-full bg-gray-100 rounded-full h-0.5 mb-2">
                    <div className="bg-primary h-0.5 rounded-full animate-pulse"></div>
                </div>
                <p className="text-center py-4 text-xs text-gray-500">Loading Dashboard...</p>
            </div>
        );
    }

    if (!data.users_stats) {
        return <p className="text-center py-4 text-xs text-gray-500">Loading Dashboard...</p>;
    }

    {/* Icon mapping function */ }
    const getStatIcon = (title) => {
        const iconSize = 24;
        const iconClass = "text-lg";

        const icons = {
            "Total Users": <FaUsers className={iconClass} size={iconSize} />,
            "Total Staff Users": <FaUserTie className={iconClass} size={iconSize} />,
            "Total Dukaan Users": <FaStore className={iconClass} size={iconSize} />,
            "Total Farms": <FaTractor className={iconClass} size={iconSize} />,
            "Total Satellite Records": <FaSatellite className={iconClass} size={iconSize} />,
            "Total Commodities": <FaBoxes className={iconClass} size={iconSize} />,
            "Total Practice Commodities": <FaSeedling className={iconClass} size={iconSize} />,
            "Total FPO": <FaUsers className={iconClass} size={iconSize} />,
            "Total User Devices": <FaMobileAlt className={iconClass} size={iconSize} />,
            "Total Shops": <FaStore className={iconClass} size={iconSize} />,
            "Total Manufacturers": <FaIndustry className={iconClass} size={iconSize} />,
            "Total Categories": <FaTags className={iconClass} size={iconSize} />,
            "Total Products": <FaBoxes className={iconClass} size={iconSize} />

        };

        return icons[title] || <FaChartBar className={iconClass} size={iconSize} />;
    };

    {/* Color mapping function */ }
    const getStatColor = (title) => {
        const colors = {
            "Total Users": "violet",
            "Total Staff Users": "teal",
            "Total Dukaan Users": "emerald",
            "Total Farms": "amber",
            "Total Satellite Records": "violet",
            "Total Commodities": "green",
            "Total Practice Commodities": "teal",
            "Total FPO": "emerald",
            "Total User Devices": "amber",
            "Total Shops": "violet",
            "Total Manufacturers": "amber",
            "Total Categories": "teal",
            "Total Products": "emerald",
        };

        return colors[title] || "green";
    };

    return (
        <div className="p-2 space-y-4 mx-auto">
            {/* First Row - Compact Stats */}
            <div className="flex md:flex-row flex-col gap-3">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 w-full">
                    {data.users_stats?.map((stat) => (
                        <CompactStatsCard
                            key={stat.title}
                            title={stat.title}
                            value={stat.value}
                            icon={getStatIcon(stat.title)}
                            color={getStatColor(stat.title)}
                        />
                    ))}
                </div>

                {/* Pie Chart */}
                <div className="bg-white rounded-lg shadow-xs border p-2 w-full max-w-md">
                    <SectionHeader title="State Distribution" icon={<FaMapMarkerAlt />} />
                    <div className="h-40">
                        <TopRegionsMap salesData={(data.state_wise || []).reduce((acc, item) => ({ ...acc, [item.state]: item.user_count }), {})} scale={290} height={180} bottomLabel={false} />
                        {/* <PieChart
                            data={getChartData(data.state_wise || [])}
                            selectedKey="user_count"
                        /> */}
                    </div>
                </div>
            </div>

            {/* Second Row - Charts */}
            <div className="grid md:grid-cols-2 grid-cols-1 gap-3">
                <div className="bg-white rounded-lg shadow-xs border p-2 w-full">
                    <SectionHeader title="Monthly Metrics" icon={<FaCalendarAlt />} />
                    <div className="h-64">
                        <BarChart data={data.monthly} />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className='bg-white rounded-lg shadow-xs border p-4'>
                        <div className="flex justify-between items-center mb-2">
                            <SectionHeader title="MOST SELLED PRODUCTS" subTitle='(Today)' icon={<Package />} />
                        </div>
                        <ul className="space-y-3">
                            {data.products.top_selling_products.slice(0, 5).map((item, index, array) => {
                                // Find the maximum value in the entire array for proper relative scaling
                                const maxValue = Math.max(...array.map(product => product.total_quantity || 0));
                                const widthPercent = maxValue > 0 ? `${((item.total_quantity || 0) / maxValue) * 100}%` : '0%';
                                return (
                                    <li key={index}>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="truncate w-2/3" title={item.name}>{item.name}</span>
                                            <span className="font-semibold">{item.total_quantity}</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded h-1 mt-1">
                                            <div className="bg-primary h-1 rounded" style={{ width: widthPercent }} />
                                        </div>
                                    </li>
                                );
                            })}
                            <div className="mt-2 text-primary text-sm cursor-pointer" onClick={() => openModal(data.products.top_selling_products, "Top Selling Products")}>View pages and screens →</div>
                        </ul>
                    </div>

                    <div className='bg-white rounded-lg shadow-xs border p-4'>
                        <div className="flex justify-between items-center mb-2">
                            <SectionHeader title="MOST VIEWED PRODUCTS" subTitle='(This month)' icon={<Package />} />

                        </div>
                        <ul className="space-y-3">
                            {data.products.most_visited_products_this_month.slice(0, 5)
                                .map((item, index, array) => {
                                    // Find the maximum value in the entire array for proper relative scaling
                                    const maxValue = Math.max(...array.map(product => product.total_visits || 0));
                                    const widthPercent = maxValue > 0 ? `${((item.total_visits || 0) / maxValue) * 100}%` : '0%';
                                    return (
                                        <li key={index}>
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="truncate w-2/3" title={item.name}>{item.name}</span>
                                                <span className="font-semibold">{item.total_visits}</span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded h-1 mt-1">
                                                <div className="bg-primary h-1 rounded" style={{ width: widthPercent }} />
                                            </div>
                                        </li>
                                    );
                                })}
                            <div className="mt-2 text-primary text-sm cursor-pointer" onClick={() => openModal(data.products.most_visited_products_this_month, "Most Viewed Products")}>View events →</div>

                        </ul>
                    </div>
                </div>
            </div>

            {/* Third Row */}
            <div className="flex md:flex-row flex-col gap-3">
                <div className="flex md:flex-row flex-col gap-3 flex-1">
                    {/* Show revenue for B2B and B2C Here */}
                    <div className="bg-white flex-1 rounded-lg shadow-xs border p-2 w-full">
                        <SectionHeader title="Revenue (Quaterly)" icon={<FaRupeeSign />} />
                        <div className="h-64">
                            <BarChartSales data={data.monthly_sale} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-2 max-w-48">
                        {data.bazaar?.map((stat, index) => (
                            <CompactStatsCard
                                key={`bazaar-${index}`}
                                title={stat.title}
                                value={stat.value}
                                icon={getStatIcon(stat.title)}
                                color={getStatColor(stat.title)}
                                trend={stat.title.includes('Active') ? 'up' : 'neutral'}
                            />
                        ))}
                    </div>
                </div>

                {/* User activity */}
                {/* <div className="bg-white rounded-lg shadow-xs border p-2">
                    <div className="flex justify-between items-center mb-2">
                        <SectionHeader title="Recent Activity" icon={<FaUsers />} />
                        <span className="text-md font-bold text-primary">
                            (24h: {data.online_users_last_24_hours?.length || 0})
                        </span>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                        <UserActivityTable data={data.online_users_last_24_hours || []} />
                    </div>
                </div> */}
            </div>
            <ShowModal
                contentLabel={"Products Alert"}
                isOpen={modalIsOpen}
                modalWidth="30%"
                isSidebar={true}
                onRequestClose={closeModal}
                componentToPass={
                    <div>
                        <h3 className="text-lg font-bold mb-4">{openingItemTitle}</h3>
                        <ul className="space-y-3">
                            {openingItem.map((item, index, array) => {
                                // Find the maximum value in the entire array for proper relative scaling
                                const maxValue = Math.max(...array.map(product => product.total_quantity || product.total_visits || 0));
                                const widthPercent = maxValue > 0 ? `${((item.total_quantity || item.total_visits || 0) / maxValue) * 100}%` : '0%';
                                return (
                                    <li key={index}>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="truncate w-2/3" title={item.name}>{item.name}</span>
                                            <span className="font-semibold">{item.total_quantity || item.total_visits}</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded h-1 mt-1">
                                            <div className="bg-primary h-1 rounded" style={{ width: widthPercent }} />
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                } />
        </div>
    );
};

export default StatsDashboardView;